import { z } from "zod";

// The C↔E consent contract (Lane C A3). Lane C's /authorize handler SSRs a ConsentRequest into Lane E's
// consent screen; the screen POSTs a ConsentDecision back. Promoted here (the shared source of truth) from
// the apps/auth consent-form mock at the A3 wire-up so both the producer (Lane C) and the renderer (Lane E)
// type against one definition.
//
// Durations: the screen shows BOTH the grant lifetime AND the minted key's TTL (founder call) — so the
// contract carries `grantExpiresAt` (the ~90d refresh/grant ceiling, ISO 8601) AND `keyTtlSeconds` (the
// ~24h access-key TTL), not a single ambiguous `expiresAt`.

/** The grant-summary Lane C's /authorize SSRs; the screen renders it read-only (no per-scope checklist). */
export const ConsentRequestSchema = z.object({
  /** Opaque id of this pending authorization; echoed back with the decision. */
  requestId: z.string(),
  /** Single-use anti-CSRF token bound to this request + the auth session; echoed with the decision. */
  csrfToken: z.string(),
  /** Which flow asked for consent. Loopback PKCE still shows this screen (deliberate-grant model). */
  flow: z.enum(["pkce_loopback", "device_code"]),
  /**
   * The requesting client. `name` is the client's self-asserted, sanitized display label — NEVER trusted as
   * identity. `identityDomain` is the un-spoofable proven-controlled domain (a CIMD client's host), or null
   * for a client with no proven domain (opaque DCR / loopback-only). `verified` is true only for a client on
   * the curated vetted list; otherwise the screen shows an "unverified app" treatment and leans on the
   * origin. The consent screen must never render a client-supplied logo/URL.
   */
  client: z.object({
    id: z.string(),
    name: z.string(),
    identityDomain: z.string().nullable(),
    verified: z.boolean(),
  }),
  /**
   * Where the authorization code will be delivered — shown verbatim (MCP spec: the AS MUST display the
   * redirect hostname). `host` is null only if the sealed redirect_uri is unparseable. `isLoopback` drives
   * the "this app runs on your machine" note (loopback carries no cross-origin phishing surface, but CIMD
   * can't prevent localhost impersonation, so the user is told where the code goes).
   */
  redirect: z.object({ host: z.string().nullable(), isLoopback: z.boolean() }),
  /** Present for the device-code flow: the device the user-code was entered on. */
  device: z.object({ name: z.string() }).optional(),
  /** The org the grant is for — the DEFAULT selection (the user's first/personal org). */
  org: z.object({ id: z.string(), name: z.string() }),
  /**
   * Every org the consenting user belongs to — the candidate list the screen may offer (Lane 2.4). Sealed
   * into the consent ticket at /authorize time from the user's live memberships, so the decision endpoint
   * can VALIDATE a form-supplied choice against it rather than trusting the page. One entry means no choice
   * to make (the screen renders the org read-only); more than one means the user picks which org the app is
   * being authorized for. Always contains `org`.
   */
  orgOptions: z.array(z.object({ id: z.string(), name: z.string() })).min(1),
  /**
   * Where the request originates — a trust signal. `location` is the best-effort 2-letter country (may be
   * null). `city`/`region`/`regionCode` are best-effort geo from the edge — OPTIONAL + nullable (additive;
   * absent in dev/test or when the edge resolved none). The screen can render e.g. "Lisbon, PT 🇵🇹" (derive
   * the flag from the 2-letter `location` via Unicode regional-indicator chars).
   */
  origin: z.object({
    ip: z.string(),
    location: z.string().nullable(),
    city: z.string().nullish(),
    region: z.string().nullish(),
    regionCode: z.string().nullish(),
  }),
  /** The requested capability scopes — rendered as a read-only summary. */
  scopes: z.array(z.string()),
  /** The resource the resulting token is audience-bound to (e.g. "https://api.webhook.co"). */
  audience: z.string(),
  /** ISO 8601 — when the GRANT (the ~90d refresh lifetime ceiling) expires if approved. */
  grantExpiresAt: z.string(),
  /** The minted access key's TTL in seconds (~24h) — shown alongside the grant lifetime. */
  keyTtlSeconds: z.number().int().positive(),
});
export type ConsentRequest = z.infer<typeof ConsentRequestSchema>;

/** The decision the consent screen POSTs back to Lane C's /authorize decision endpoint. */
export const ConsentDecisionSchema = z.object({
  requestId: z.string().min(1),
  csrfToken: z.string().min(1),
  decision: z.enum(["approve", "deny"]),
  /**
   * The org the user picked, when the screen offered a choice. UNTRUSTED — the decision endpoint MUST check
   * it against the candidate list sealed in the ticket and refuse anything else; a page cannot widen which
   * org an app is authorized for. Absent ⇒ the ticket's default org.
   */
  orgId: z.string().min(1).optional(),
});
export type ConsentDecision = z.infer<typeof ConsentDecisionSchema>;
